// The detection eval history panel, drawn at the top of /analytics/quality.
//
// This is the console's only measurement of the scope's third success target,
// scam detection accuracy above 90%. It is BENCHMARK accuracy on the held-out
// Scam-KG runs, not accuracy on live conversations: nothing labels live
// traffic, so production accuracy is not measured anywhere.
//
// The fraud scope excludes the promo-spam benchmark. Every rate the server
// sends is null rather than 0 when its denominator was empty, and pctOrDash
// keeps it that way on screen.

import { useMemo, useState } from "react";

import { http, qs } from "../api";
import { Banner, Empty, SkeletonRows, Spinner, useAsync } from "../ui";
import { BarChartCard, LineChartCard, StatTile } from "../charts";
import { STATUS } from "../theme";
import { pctOrDash } from "./shared";

export interface EvalTrendPoint {
  run_id: string;
  ts?: string;
  n?: number;
  fraud_n?: number;
  fraud_recall?: number | null;
  fraud_precision?: number | null;
  fraud_f1?: number | null;
}

interface EvalCaseRow {
  case_id: string;
  source?: string;
  language?: string;
  passed?: boolean;
  failure_mode?: string;
  expected_label?: string;
  kg_label?: string;
  is_scam?: boolean;
}

interface EvalRunRow {
  run_id: string;
}

const SOURCE_ORDER = ["scenario", "ds_quality", "ds_quality_parity", "sms_heldout"];

/** The Scam-KG detection eval trend, from the same store Eval Ops drills into. */
export function EvalHistoryPanel() {
  const [version, setVersion] = useState(0);

  const query = useAsync(async () => {
    const [trendRes, runsRes] = await Promise.all([
      http.get<{ trend: EvalTrendPoint[] }>(`/chatbot/eval-ops/trend${qs({ limit: 50 })}`),
      http.get<{ runs: EvalRunRow[] }>(`/chatbot/eval-ops/runs${qs({ limit: 1 })}`),
    ]);
    const trend = (trendRes.trend ?? []).filter((p) => p && p.fraud_recall != null);
    const newest = (runsRes.runs ?? [])[0];
    let cases: EvalCaseRow[] = [];
    if (newest?.run_id) {
      const caseRes = await http.get<{ run_id: string; cases: EvalCaseRow[] }>(
        `/chatbot/eval-ops/runs/${encodeURIComponent(newest.run_id)}/cases`
      );
      cases = caseRes.cases ?? [];
    }
    return { trend, cases };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const trend = query.data?.trend ?? [];
  const cases = query.data?.cases ?? [];

  const latest = trend[trend.length - 1] ?? null;
  // The comparison run must have the SAME test-set size, or the delta is really
  // "the benchmark got bigger" wearing a regression's clothes.
  const prior = useMemo(() => {
    if (!latest) return null;
    for (let i = trend.length - 2; i >= 0; i--) {
      if (trend[i].fraud_n === latest.fraud_n) return trend[i];
    }
    return null;
  }, [trend, latest]);

  const delta = (cur?: number | null, prev?: number | null) =>
    cur === null || cur === undefined || prev === null || prev === undefined
      ? null
      : (Number(cur) - Number(prev)) * 100;

  const kpis = [
    { label: "Fraud recall", value: pctOrDash(latest?.fraud_recall), d: delta(latest?.fraud_recall, prior?.fraud_recall) },
    { label: "Precision", value: pctOrDash(latest?.fraud_precision), d: delta(latest?.fraud_precision, prior?.fraud_precision) },
    { label: "F1", value: pctOrDash(latest?.fraud_f1), d: delta(latest?.fraud_f1, prior?.fraud_f1) },
  ];

  const passCount = cases.filter((c) => c.passed).length;

  const grouped = useMemo(() => {
    const bySource: Record<string, EvalCaseRow[]> = {};
    for (const c of cases) {
      const key = c.source || "other";
      (bySource[key] ||= []).push(c);
    }
    const keys = [
      ...SOURCE_ORDER.filter((s) => bySource[s]),
      ...Object.keys(bySource).filter((s) => !SOURCE_ORDER.includes(s)),
    ];
    return keys.map((src) => ({
      src,
      rows: bySource[src],
      passed: bySource[src].filter((c) => c.passed).length,
    }));
  }, [cases]);

  return (
    <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
      <div className="row">
        <div>
          <h3>Detection eval history</h3>
          <div className="muted">
            Scam-KG held-out runs: fraud recall, precision, F1 and each case pass/fail. The fraud
            scope excludes the promo-spam benchmark.
          </div>
        </div>
        <span className="spacer" />
        <button className="btn sm" disabled={query.loading} onClick={() => setVersion((v) => v + 1)}>
          {query.loading ? <Spinner label="Refreshing" /> : "⟲"} Refresh
        </button>
      </div>

      {query.error && (
        <Banner tone="warning" traceId={query.error.traceId}>
          Could not load the eval history: {query.error.message}
        </Banner>
      )}

      {query.loading ? (
        <SkeletonRows n={4} cols={3} />
      ) : trend.length === 0 ? (
        <Empty
          title="No eval runs recorded yet"
          body="Record one with scripts/eval-tracked.sh, then this trend fills in."
          icon="✓"
        />
      ) : (
        <>
          <div className="grid c4" style={{ marginTop: "var(--sp-3)" }}>
            {kpis.map((k) => (
              <StatTile
                key={k.label}
                label={k.label}
                value={k.value}
                delta={k.d === null ? undefined : k.d / 100}
                deltaLabel={k.d === null ? "no comparable prior" : "pts vs prior run"}
              />
            ))}
            <StatTile
              label="Cases passed"
              value={`${passCount}/${cases.length}`}
              deltaLabel="latest run"
            />
          </div>

          <div className="grid c2" style={{ marginTop: "var(--sp-4)" }}>
            <div>
              <LineChartCard
                title="Detection quality per run"
                yLabel="Rate"
                note="Recall and F1 share one axis because both are rates."
                series={[
                  {
                    name: "Fraud recall",
                    points: trend.map((p) => ({ x: p.run_id, y: p.fraud_recall ?? null })),
                  },
                  {
                    name: "Fraud F1",
                    points: trend.map((p) => ({ x: p.run_id, y: p.fraud_f1 ?? null })),
                  },
                ]}
              />
            </div>
            <div>
              {/* Test-set size is a count, not a rate, so it gets its own chart
                  rather than a second axis on the one above. A step down in the
                  rates where these bars grow is the benchmark expanding. */}
              <BarChartCard
                title="Test-set size per run"
                yLabel="Cases"
                data={trend.map((p) => ({ label: p.run_id, value: p.n ?? 0 }))}
              />
            </div>
          </div>

          {cases.length > 0 && (
            <div style={{ marginTop: "var(--sp-4)" }}>
              <div className="row">
                <h4>Per-case results · latest run</h4>
                <span className="spacer" />
                <span className="muted">
                  {passCount} passed · {cases.length - passCount} failed
                </span>
              </div>
              {grouped.map((g) => (
                <div key={g.src} style={{ marginTop: "var(--sp-2)" }}>
                  <div className="row">
                    <span className="strong">{g.src}</span>
                    <span className="muted num">
                      {g.passed}/{g.rows.length} passed
                    </span>
                  </div>
                  <div className="case-grid">
                    {g.rows.map((c) => (
                      <span
                        key={c.case_id}
                        className="cell"
                        style={{ background: c.passed ? STATUS.good : STATUS.critical }}
                        title={`${c.case_id}\n${c.expected_label || "·"} → ${c.kg_label || "·"}\n${
                          c.passed ? "passed" : c.failure_mode || "failed"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
